import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { Button } from './ui/button';

const WEDDING_DATE = new Date('2026-10-02T17:00:00+02:00');

const HeroSection = () => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const now = new Date();
    const difference = WEDDING_DATE - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleAddToCalendar = () => {
    const event = {
      title: 'Boda Carolina & Luciano',
      start: '20261002T170000',
      end: '20261003T020000',
      location: 'El Prat Nou, s/n, 08519 Malla, Barcelona',
      description: 'Celebración de la boda de Carolina y Luciano',
    };
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start}/${event.end}&location=${encodeURIComponent(event.location)}&details=${encodeURIComponent(event.description)}&ctz=Europe/Madrid`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: 'easeOut' },
    },
  };

  return (
    <section 
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative bg-gradient-to-b from-[#F9F7F2] via-[#F9F7F2] to-[#F5F0EB]"
      data-testid="hero-section"
    >
      <motion.div
        className="max-w-4xl mx-auto text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Couple Image - Original size */}
        <motion.div 
          className="mb-8 md:mb-12"
          variants={itemVariants}
        >
          <div className="mx-auto overflow-hidden shadow-xl border-4 border-white/80 inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_nuestra-boda/artifacts/g9u9pti7_Captura%20de%20pantalla%202026-02-05%20214943.jpg"
              alt="Carolina y Luciano"
              className="max-w-full h-auto"
              data-testid="couple-image"
            />
          </div>
        </motion.div>

        {/* Main Title */}
        <motion.h1 
          className="font-accent text-4xl md:text-5xl lg:text-6xl text-[#5D4037] mb-6"
          variants={itemVariants}
          data-testid="main-title"
        >
          Te invitamos a nuestra boda
        </motion.h1>

        {/* Date with decorative elements */}
        <motion.div 
          className="flex items-center justify-center gap-3 mb-10"
          variants={itemVariants}
        >
          <div className="w-8 md:w-12 h-[1px] bg-gradient-to-r from-transparent to-[#CCA598]" />
          <p 
            className="font-body text-base md:text-lg text-[#8D6E63] tracking-[0.2em] uppercase"
            data-testid="wedding-date"
          >
            2 de Octubre de 2026
          </p>
          <div className="w-8 md:w-12 h-[1px] bg-gradient-to-l from-transparent to-[#CCA598]" />
        </motion.div>

        {/* Countdown with background */}
        <motion.div 
          className="inline-flex justify-center items-center gap-3 md:gap-6 mb-10 bg-[#F5F0EB]/80 px-6 py-4 md:px-10 md:py-5 rounded-sm shadow-sm"
          variants={itemVariants}
          data-testid="countdown"
        >
          <div className="countdown-item">
            <span className="countdown-number !font-['Times_New_Roman'] text-[#5D4037]">{timeLeft.days}</span>
            <span className="countdown-label text-[#8D6E63]">Días</span>
          </div>
          <span className="text-[#CCA598] text-xl md:text-2xl font-light">:</span>
          <div className="countdown-item">
            <span className="countdown-number !font-['Times_New_Roman'] text-[#5D4037]">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="countdown-label text-[#8D6E63]">Horas</span>
          </div>
          <span className="text-[#CCA598] text-xl md:text-2xl font-light">:</span>
          <div className="countdown-item">
            <span className="countdown-number !font-['Times_New_Roman'] text-[#5D4037]">{String(timeLeft.minutes).padStart(2, '0')}</span>
            <span className="countdown-label text-[#8D6E63]">Min</span>
          </div>
          <span className="text-[#CCA598] text-xl md:text-2xl font-light">:</span>
          <div className="countdown-item">
            <span className="countdown-number !font-['Times_New_Roman'] text-[#5D4037]">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="countdown-label text-[#8D6E63]">Seg</span>
          </div>
        </motion.div>

        {/* Add to Calendar Button */}
        <motion.div variants={itemVariants}>
          <Button
            onClick={handleAddToCalendar}
            variant="outline"
            className="font-body text-sm tracking-wider uppercase border-[#CCA598] text-[#5D4037] hover:bg-[#CCA598]/10 hover:border-[#8D6E63] transition-all duration-300 px-6 py-3 rounded-none"
            data-testid="add-to-calendar-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Añadir a calendario
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
